import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import Box from '@cloudscape-design/components/box';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import {
  formatCurrencyCents,
  formatInt,
  formatUsdCents,
} from '@/features/dashboard-finance/formatters';
import type { CancelledTenantRow } from './useSalesMetrics';
import { TableFilters } from './TableFilters';

function formatDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Tabela dos tenants cancelados no período, com a receita direta mensal que
 * levaram e o tempo de vida.
 */
export function CancelledTenantsTable({
  rows,
}: {
  rows: readonly CancelledTenantRow[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const country = params.get('cancelledCountry') ?? '';
  const currency = params.get('cancelledCurrency') ?? '';

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (country && r.countryCode !== country) return false;
        if (currency && r.currency !== currency) return false;
        return true;
      }),
    [rows, country, currency],
  );

  return (
    <Table<CancelledTenantRow>
      items={filtered}
      trackBy={(r): string => `${r.tenantId}-${String(r.suspendedAt)}`}
      variant="container"
      header={
        <Header
          variant="h3"
          actions={<TableFilters rows={rows} countryParam="cancelledCountry" currencyParam="cancelledCurrency" />}
        >
          {t('sales.tables.cancelled.title', { count: filtered.length })}
        </Header>
      }
      empty={<Box textAlign="center" color="inherit">{t('sales.empty')}</Box>}
      columnDefinitions={[
        { id: 'name', header: t('sales.tables.cancelled.name'), cell: (r): React.ReactNode => r.tenantName },
        { id: 'country', header: t('sales.tables.cancelled.country'), cell: (r): React.ReactNode => r.countryCode },
        { id: 'currency', header: t('sales.tables.cancelled.currency'), cell: (r): React.ReactNode => r.currency },
        {
          id: 'price',
          header: t('sales.tables.cancelled.price'),
          cell: (r): React.ReactNode => formatCurrencyCents(r.priceCents, r.currency),
        },
        {
          id: 'usdEq',
          header: t('sales.tables.cancelled.usdEq'),
          cell: (r): React.ReactNode => formatUsdCents(r.usdEqCents),
        },
        {
          id: 'suspendedAt',
          header: t('sales.tables.cancelled.suspendedAt'),
          cell: (r): React.ReactNode => formatDate(r.suspendedAt),
        },
        {
          id: 'lifetime',
          header: t('sales.tables.cancelled.lifetime'),
          cell: (r): React.ReactNode => (r.lifetimeDays == null ? '—' : formatInt(r.lifetimeDays)),
        },
      ]}
    />
  );
}
